// Timeline data-source seam. The page and TimelineView never import spec JSON —
// they go through these hooks; specs come from the live endpoints only:
//   GET /api/v1/timeline        -> TimelineMeta[]
//   GET /api/v1/timeline/{id}   -> TimelineSpec
//
// The bundled Marvel demo snapshots that used to backfill an empty list were
// removed 2026-08-06 (audit D2): dataset-specific content in the product, and
// the page described them as "derived by a real query over ... the graph"
// when nothing in the store produced them. An instance with no temporal tags
// now gets an honest empty state instead of someone else's comics.

import { useQuery } from '@tanstack/react-query';
import { authHeaders } from '../api';
import type { TimelineSpec } from './types';
import { useApiSession } from '@/lib/useApiSession';

export interface TimelineMeta {
  id: string;
  label: string;
}

async function fetchList(token: string | undefined): Promise<TimelineMeta[]> {
  // No early return on a missing token: a signed-out visitor on an instance
  // that serves them gets the read-only anonymous role, and returning [] here
  // showed them an instance with no timelines rather than the ones it has.
  const res = await fetch('/api/v1/timeline', { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`timelines: HTTP ${res.status}`);
  return (await res.json()) as TimelineMeta[];
}

async function fetchSpec(id: string, token: string | undefined): Promise<TimelineSpec> {
  const res = await fetch(`/api/v1/timeline/${encodeURIComponent(id)}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`timeline ${id}: HTTP ${res.status}`);
  return (await res.json()) as TimelineSpec;
}

export function useTimelineList() {
  // These two run unconditionally — the timeline list is a plain read, and a
  // guest gets the anonymous role's view of it rather than an empty array.
  const { token } = useApiSession();
  return useQuery<TimelineMeta[]>({
    queryKey: ['timelines', !!token],
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchList(token),
  });
}

export function useTimeline(id: string | undefined) {
  const { token } = useApiSession();
  return useQuery<TimelineSpec>({
    queryKey: ['timeline', id],
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchSpec(id as string, token),
  });
}
